import { useState, useCallback } from 'react';
import { apiClient } from '../index';

export interface DeliveryReportData {
  date: string;
  campaignName: string;
  campaignsSent: number;
  delivered: number;
  deliveredRatio: number;
  deliveredPercent: number;
  opens: number;
  opensPercent: number;
  clicks: number;
  clicksPercent: number;
  bounced: number;
  bouncedPercent: number;
  unsubscribed: number;
  unsubscribedPercent: number;
}

export interface DeliveryReportStatus {
  status: 'idle' | 'processing' | 'completed' | 'failed';
  workflowId?: string;
  message?: string;
  campaignsProcessed?: number;
}

export const useDeliveryReport = () => {
  const [data, setData] = useState<DeliveryReportData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [regenerating, setRegenerating] = useState(false);

  const fetchDeliveryReport = useCallback(async (params?: {
    days?: number;
    startDate?: string;
    endDate?: string;
    topicId?: number;
  }) => {
    setLoading(true);
    setError(null);
    
    try {
      const queryParams = new URLSearchParams();
      
      if (params?.days) {
        queryParams.append('days', params.days.toString());
      }
      if (params?.startDate) {
        queryParams.append('startDate', params.startDate);
      }
      if (params?.endDate) {
        queryParams.append('endDate', params.endDate);
      }
      if (params?.topicId) {
        queryParams.append('topic_id', params.topicId.toString());
      }
      
      const url = `/api/delivery-report${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const result = await apiClient.get<DeliveryReportData[]>(url);
      setData(result);
      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch delivery report';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const regenerateReport = useCallback(async (params: {
    startDate: string;
    endDate: string;
  }) => {
    setRegenerating(true);
    setError(null);
    
    try {
      const result = await apiClient.post<DeliveryReportStatus>('/api/delivery-report/recalculate', params);
      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to regenerate delivery report';
      setError(errorMessage);
      throw err;
    } finally {
      setRegenerating(false);
    }
  }, []);

  return {
    data,
    loading,
    error,
    regenerating,
    fetchDeliveryReport,
    regenerateReport,
  };
};